// src/components/test/PrivateFileTest.tsx
import React, { useState } from 'react';
import './PrivateFileTest.scss';
import { getCmsUrl } from '../../services/configService';

export const PrivateFileTest: React.FC = () => {
  const [result, setResult] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const simpleTest = async () => {
    setLoading(true);
    setResult('Downloading...');

    try {
      // Fetch from Drupal - proxied in dev, direct in prod
      const cmsBaseUrl = getCmsUrl();
      const fileUrl = `${cmsBaseUrl}/system/files/test-document.txt`;
      const response = await fetch(fileUrl, {
        credentials: 'include',
        redirect: 'manual',  // Prevent auto-redirect to FusionAuth (CORS issue)
      });

      // Check for redirect (user not authenticated)
      if (response.status === 0 || response.type === 'opaqueredirect') {
        setResult('❌ Not authenticated - redirecting to login...');
        const returnUrl = window.location.href;
        window.location.href = `${cmsBaseUrl}/user/login/sso?return_to=${encodeURIComponent(returnUrl)}`;
        return;
      }

      if (response.ok) {
        const blob = await response.blob();
        const text = await blob.text();

        // Create download
        const downloadBlob = new Blob([text], { type: 'text/plain' });
        const url = URL.createObjectURL(downloadBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'test-document.txt';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setResult(`✅ SUCCESS! File downloaded. Content: "${text}"`);
      } else {
        setResult(`❌ Failed: HTTP ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      setResult(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    // Direct navigation to Drupal SSO (proxied in dev, direct in prod)
    const cmsBaseUrl = getCmsUrl();
    const returnUrl = window.location.href;
    window.location.href = `${cmsBaseUrl}/user/login/sso?return_to=${encodeURIComponent(returnUrl)}`;
  };

  const mode = import.meta.env.DEV ? 'Development (Proxied)' : 'Production (Direct)';

  return (
    <div className="private-file-test">
      <h2>Simple Private File Test</h2>
      <p>Proof of concept: Can we access Drupal private files?</p>
      <p className="mode-badge">Mode: <strong>{mode}</strong></p>

      <div className="test-actions">
        <button onClick={goToLogin} className="btn-primary">
          1. Login via Drupal SSO
        </button>
        <button onClick={simpleTest} disabled={loading} className="btn-primary">
          {loading ? '2. Downloading...' : '2. Download Test File'}
        </button>
      </div>

      {result && (
        <div className="test-result">
          <h3>Result:</h3>
          <pre>{result}</pre>
        </div>
      )}

      <div className="instructions">
        <h3>Steps:</h3>
        <ol>
          <li>Click "Login via Drupal SSO" - you'll go through auth and come back</li>
          <li>Click "Download Test File" - downloads the private file from Drupal</li>
          <li>If it works, the file will download and you'll see the content below!</li>
        </ol>
      </div>
    </div>
  );
};
